import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { calculateEmployeeTaxes } from '@/components/utils/dynamicTaxCalculations';
import { Receipt, TrendingDown, TrendingUp } from 'lucide-react';

export default function PayStubPreview({ employee, monthlyPremium = 0 }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (employee && employee.annual_salary) {
      calculatePreview();
    } else {
      setLoading(false);
      setPreview(null);
    }
  }, [employee, monthlyPremium]);

  const calculatePreview = async () => {
    if (!employee || !employee.annual_salary) {
      setLoading(false);
      return;
    }

    try {
      const annualPremium = monthlyPremium * 12;
      
      // Calculate with Section 125
      const withSection125 = await calculateEmployeeTaxes(employee, annualPremium).catch(err => {
        console.warn('Tax calculation error:', err.message);
        return null;
      });
      
      // Calculate without Section 125
      const withoutSection125 = await calculateEmployeeTaxes(employee, 0).catch(err => {
        console.warn('Tax calculation error:', err.message);
        return null;
      });
      
      // If tax calculations failed, show simplified preview
      if (!withSection125 || !withoutSection125) {
        // Simplified calculation without tax data
        const payFrequencyMap = {
          weekly: 52,
          bi_weekly: 26,
          semi_monthly: 24,
          monthly: 12
        };
        const periodsPerYear = payFrequencyMap[employee.pay_frequency] || 26;
        const grossPerPeriod = employee.annual_salary / periodsPerYear;
        const premiumPerPeriod = (monthlyPremium * 12) / periodsPerYear;
        
        // Estimate taxes at 25% if we don't have config
        const estimatedTaxRate = 0.25;
        const taxesPerPeriodBefore = grossPerPeriod * estimatedTaxRate;
        const taxableAfterSection125 = grossPerPeriod - premiumPerPeriod;
        const taxesPerPeriodAfter = taxableAfterSection125 * estimatedTaxRate;
        
        setPreview({
          grossPerPeriod,
          premiumPerPeriod,
          taxesPerPeriodBefore,
          taxesPerPeriodAfter,
          taxSavingsPerPeriod: taxesPerPeriodBefore - taxesPerPeriodAfter,
          netPayBefore: grossPerPeriod - taxesPerPeriodBefore,
          netPayAfter: grossPerPeriod - premiumPerPeriod - taxesPerPeriodAfter,
          netDifference: (grossPerPeriod - premiumPerPeriod - taxesPerPeriodAfter) - (grossPerPeriod - taxesPerPeriodBefore),
          periodsPerYear,
          payFrequency: employee.pay_frequency || 'bi_weekly',
          annualTaxSavings: (taxesPerPeriodBefore - taxesPerPeriodAfter) * periodsPerYear,
          isEstimated: true
        });
        setLoading(false);
        return;
      }
      
      // Determine pay frequency
      const payFrequencyMap = {
        weekly: 52,
        bi_weekly: 26,
        semi_monthly: 24,
        monthly: 12
      };
      const periodsPerYear = payFrequencyMap[employee.pay_frequency] || 26;
      
      const grossPerPeriod = employee.annual_salary / periodsPerYear;
      const premiumPerPeriod = (monthlyPremium * 12) / periodsPerYear;
      const taxesPerPeriodBefore = withoutSection125.before.totalTax / periodsPerYear;
      const taxesPerPeriodAfter = withSection125.after.totalTax / periodsPerYear;
      const netPayBefore = grossPerPeriod - taxesPerPeriodBefore;
      const netPayAfter = grossPerPeriod - premiumPerPeriod - taxesPerPeriodAfter;
      
      setPreview({
        grossPerPeriod,
        premiumPerPeriod,
        taxesPerPeriodBefore,
        taxesPerPeriodAfter,
        taxSavingsPerPeriod: taxesPerPeriodBefore - taxesPerPeriodAfter,
        netPayBefore,
        netPayAfter,
        netDifference: netPayAfter - netPayBefore,
        periodsPerYear,
        payFrequency: employee.pay_frequency || 'bi_weekly',
        annualTaxSavings: withSection125.savings.total,
        isEstimated: false
      });
    } catch (err) {
      console.error('Failed to calculate pay stub preview:', err);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!preview) {
    return (
      <Card className="border-2 border-slate-200 bg-slate-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-700">
            <Receipt className="w-5 h-5" />
            Pay Stub Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            <p className="text-sm">
              {!employee?.annual_salary 
                ? 'Salary information needed to calculate pay stub preview'
                : monthlyPremium === 0 
                  ? 'Select coverage to see pay stub preview'
                  : 'Unable to calculate preview'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatFrequency = (freq) => {
    return freq.replace('_', '-').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-');
  };

  return (
    <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-emerald-900">
          <Receipt className="w-5 h-5" />
          Pay Stub Preview ({formatFrequency(preview.payFrequency)})
        </CardTitle>
        {preview.isEstimated && (
          <p className="text-xs text-amber-700 mt-2">
            ⚠️ Using estimated tax rates. Actual taxes may vary based on your specific tax situation.
          </p>
        )}
        <div className="mt-3 p-3 bg-white rounded-lg border border-emerald-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Total Monthly Premium</p>
              <p className="text-lg font-bold text-emerald-700">${monthlyPremium.toFixed(2)}/mo</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Per Paycheck ({preview.periodsPerYear}/year)</p>
              <p className="text-lg font-bold text-slate-900">${preview.premiumPerPeriod.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Before Section 125 */}
        <div className="bg-white rounded-lg p-4 space-y-2">
          <p className="text-sm font-semibold text-slate-700 mb-3">Without Section 125 Plan</p>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Gross Pay</span>
            <span className="font-medium">${preview.grossPerPeriod.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Federal & State Tax</span>
            <span className="font-medium text-red-600">-${preview.taxesPerPeriodBefore.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm pt-2 border-t">
            <span className="font-semibold text-slate-900">Net Pay</span>
            <span className="font-bold text-slate-900">${preview.netPayBefore.toFixed(2)}</span>
          </div>
        </div>

        {/* After Section 125 */}
        <div className="bg-white rounded-lg p-4 space-y-2 border-2 border-emerald-300">
          <p className="text-sm font-semibold text-emerald-700 mb-3">With Section 125 Plan ✨</p>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Gross Pay</span>
            <span className="font-medium">${preview.grossPerPeriod.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Pre-Tax Benefit (per paycheck)</span>
            <span className="font-medium text-emerald-600">-${preview.premiumPerPeriod.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Federal & State Tax</span>
            <span className="font-medium text-red-600">-${preview.taxesPerPeriodAfter.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm pt-2 border-t">
            <span className="font-semibold text-slate-900">Net Pay</span>
            <span className="font-bold text-emerald-600">${preview.netPayAfter.toFixed(2)}</span>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg p-4 text-white">
          <p className="text-xs opacity-90 mb-3 border-b border-white/30 pb-2">
            ℹ️ All amounts below are per paycheck. You'll pay ${preview.premiumPerPeriod.toFixed(2)} each paycheck instead of paying the full ${monthlyPremium.toFixed(2)}/month.
          </p>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Tax Savings Per Paycheck</span>
            <div className="flex items-center gap-1">
              <TrendingDown className="w-4 h-4" />
              <span className="font-bold">${preview.taxSavingsPerPeriod.toFixed(2)}</span>
            </div>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Net Pay Impact Per Paycheck</span>
            <div className="flex items-center gap-1">
              {preview.netDifference >= 0 ? (
                <>
                  <TrendingUp className="w-4 h-4" />
                  <span className="font-bold">+${preview.netDifference.toFixed(2)}</span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-4 h-4" />
                  <span className="font-bold">${preview.netDifference.toFixed(2)}</span>
                </>
              )}
            </div>
          </div>
          <div className="pt-2 border-t border-white/30 mt-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">💰 Annual Tax Savings</span>
              <span className="text-lg font-bold">${preview.annualTaxSavings.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-500 text-center">
          * Estimates based on current tax tables and your profile information
        </p>
      </CardContent>
    </Card>
  );
}